import moment from "moment";
import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Table, Badge, Form, Row, Col, Button,
  InputGroup, Pagination, Spinner
} from "react-bootstrap";
import {
  LuSearch, LuArrowUpRight, LuChevronLeft, LuChevronRight,
  LuCircleDot, LuFileText, LuMail
} from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getInvoiceList } from "../../../redux/reducers/invoiceSlice";
import { formatCurrency } from "../../../utils/currency";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export default function InvoiceList({ activeStatus }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const invoicedata = useSelector((state) => state.invoice.invoiceList);
  const pagination = useSelector((state) => state.invoice.pagination);
  const loading = useSelector((state) => state.invoice.loading);
  const activeBusinessId = useSelector((state) => state.business.activeBusinessId);
  const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');

  // --- FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadData = useCallback((page = 1) => {
    if (activeBusinessId) {
      dispatch(getInvoiceList({
        businessId: activeBusinessId,
        page,
        limit,
        status: activeStatus === "All" ? "" : activeStatus,
        dateFilter: dateFilter === "all" ? "" : dateFilter,
        search: searchTerm,
      }));
    }
  }, [dispatch, activeBusinessId, activeStatus, limit, dateFilter, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
  }, [activeStatus, limit, dateFilter, activeBusinessId]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadData(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage]);

  const hasActiveFilters = searchTerm || dateFilter !== "all";

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("all");
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return <Badge bg="success-subtle" className="text-success rounded-pill px-3">Paid</Badge>;
      case "sent":
        return <Badge bg="info-subtle" className="text-info rounded-pill px-3">Sent</Badge>;
      case "draft":
        return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3">Draft</Badge>;
      case "cancelled":
        return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3">Cancelled</Badge>;
      default:
        return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3">{status}</Badge>;
    }
  };

  // Avatar initials helper
  const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const avatarColors = ["#28B0C4", "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];
  const getAvatarColor = (id = "") =>
    avatarColors[id.charCodeAt(id.length - 1) % avatarColors.length];

  return (
    <div>
      {/* --- FILTER BAR --- */}
      <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
        <Row className="g-3 align-items-end">

          <Col lg={4} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
            <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
              <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                <LuSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Customer name or email..."
                className="bg-transparent border-0 shadow-none py-2 small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col lg={2} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Date Range</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="thisweek">This Week</option>
              <option value="thismonth">This Month</option>
            </Form.Select>
          </Col>

          {hasActiveFilters && (
            <Col lg="auto">
              <Button
                variant="outline-secondary"
                className="rounded-pill px-3 small fw-bold py-2"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Col>
          )}

          <Col lg="auto" className="ms-auto">
            <div className="text-muted small fw-medium text-end">
              <strong>{pagination?.total ?? 0}</strong> invoices
            </div>
          </Col>

        </Row>
      </Card>

      {/* --- TABLE --- */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted small mt-2">Loading invoices...</p>
            </div>
          ) : (
            <Table responsive hover className="align-middle mb-0">
              <thead className="bg-light text-muted small text-uppercase">
                <tr>
                  <th className="ps-4 py-3 fw-semibold">Invoice</th>
                  <th className="fw-semibold">Customer</th>
                  <th className="fw-semibold">Amount</th>
                  <th className="fw-semibold">Status</th>
                  <th className="pe-4 text-end fw-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoicedata && invoicedata.length > 0 ? (
                  invoicedata.map((invoice) => (
                    <tr key={invoice._id}>

                      {/* Invoice */}
                      <td className="ps-4 py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div className="p-2 bg-light rounded-3 text-muted">
                            <LuFileText size={18} />
                          </div>
                          <div>
                            <div className="fw-bold small">
                              #{invoice._id?.slice(-6).toUpperCase()}
                            </div>
                            <div className="text-muted" style={{ fontSize: "11px" }}>
                              {moment(invoice?.createdAt).format("MMM DD, YYYY")}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Customer */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {invoice?.customer?.name ? (
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                              style={{
                                width: 30,
                                height: 30,
                                fontSize: "11px",
                                backgroundColor: getAvatarColor(invoice._id),
                                flexShrink: 0
                              }}
                            >
                              {getInitials(invoice.customer.name)}
                            </div>
                          ) : (
                            <div className="p-1 bg-light rounded-circle">
                              <LuMail size={14} className="text-muted" />
                            </div>
                          )}
                          <div>
                            <div className="small fw-semibold">
                              {invoice?.customer?.name || "Direct Payment"}
                            </div>
                            <div className="text-muted" style={{ fontSize: "11px" }}>
                              {invoice?.customer?.email || "No email"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td>
                        <div className="fw-bold small">
                          {formatCurrency(invoice?.totalAmount || 0, currency)}
                        </div>
                      </td>

                      {/* Status */}
                      <td>{getStatusBadge(invoice?.status)}</td>

                      {/* Action */}
                      <td className="pe-4 text-end">
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="View invoice"
                          onClick={() => navigate(`/view-invoice/${invoice._id}`)}
                        >
                          <LuArrowUpRight size={18} className="text-muted" />
                        </Button>
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      <LuCircleDot size={40} className="mb-2 opacity-25" />
                      <p className="mb-0 small">
                        {hasActiveFilters
                          ? "No invoices match your filters."
                          : "No invoices found."}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>

        {/* --- PAGINATION FOOTER --- */}
        {pagination && pagination.total > 0 && (
          <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-medium">Rows per page:</small>
                <Form.Select
                  size="sm"
                  className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                  style={{ width: "70px", fontSize: "0.8rem" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </div>
              <small className="text-muted fw-medium border-start ps-3">
                Page <strong>{pagination.page}</strong> of <strong>{pagination.pages}</strong>
                &nbsp;({pagination.total} total)
              </small>
            </div>

            <Pagination
              className="mb-0 gap-1"
              style={{
                "--bs-pagination-active-bg": "#28B0C4",
                "--bs-pagination-active-border-color": "#28B0C4",
              }}
            >
              <Pagination.Prev
                disabled={pagination.page === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="border-0 shadow-none"
              >
                <LuChevronLeft />
              </Pagination.Prev>
              {[...Array(pagination.pages || 0)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === pagination.page}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={pagination.page === pagination.pages || pagination.pages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="border-0 shadow-none"
              >
                <LuChevronRight />
              </Pagination.Next>
            </Pagination>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
}